<template>
  <div>
    <section>
      <b-tabs
        v-model="currentTabIndex"
        :animated="false"
        :class="`sticky-tabs-headers ${lowerTab ? 'lower' : ''}`"
      >
        <b-tab-item>
          <template #header>
            <TabHeaderWithHint
              :label="$t('StructureSelectionView.label-description')"
              :tab-validation-hint="tabValidationHints[0]"
            />
          </template>
          <ValidationObserver ref="observer-0">
            <InputWithValidation
              v-model="localStructure.nom"
              rules="required"
              :label="$t(`StructureSelectionView.label-nom`)"
              :placeholder="$t(`StructureSelectionView.placeholder-nom`)"
              :disabled="readonly"
              vid="nomStructure"
              :control-action="userCanChange && !readonly"
              :control-right="true"
            >
              <b-button v-if="userCanChange && !readonly" type="is-dark" @click="changeStructure">
                {{ $t("profilePage.structure.change.change") }}
              </b-button>
            </InputWithValidation>
            <ValidationProvider
              v-slot="{ errors, valid, handleChange, handleBlur }"
              :name="$t('StructureSelectionView.label-type')"
              rules="required"
              :model-value="localStructure.type"
              @update:model-value="updateStructureType"
            >
              <b-field
                :type="{
                  'is-normal': readonly,
                  'is-danger': errors[0],
                  'is-success': valid,
                }"
                :message="errors"
              >
                <template #label>
                  <div class="fieldLabel">
                    <div class="innerLabel">{{ $t("StructureSelectionView.label-type") }}</div>
                    <div v-if="!readonly">
                      <div class="mandatory">{{ $t("obligatoire") }}</div>
                    </div>
                  </div>
                </template>

                <b-select
                  v-if="!readonly"
                  :placeholder="$t(`StructureSelectionView.placeholder-type`)"
                  :model-value="localStructure.type"
                  @update:model-value="
                    (val: any) => {
                      handleChange && handleChange(val)
                      setStructureType(val)
                    }
                  "
                  @blur="(event: any) => handleBlur && handleBlur(event)"
                >
                  <template v-for="(option, i) in structureTypesRadios" :key="option">
                    <option :value="option">
                      {{ structureTypesRadiosLabels[i] }}
                    </option>
                  </template>
                  <optgroup :label="$t(`StructureSelectionView.types-label-aire-protegee`)">
                    <template v-for="(aire, i) in aireProtegeeTypes" :key="aire">
                      <option :value="aire">
                        {{ aireProtegeeTypesLabels[i] }}
                      </option>
                    </template>
                  </optgroup>
                  <option value="AUTRE">
                    {{ $t(`StructureSelectionView.label-autre`) }}
                  </option>
                </b-select>

                <b-input
                  v-else
                  v-model="localStructure.type"
                  :disabled="readonly"
                  custom-class="SelectWithValidation-disabled"
                ></b-input>
              </b-field>
            </ValidationProvider>

            <InputWithValidation
              v-if="displayOther"
              v-model="localStructure.autreType"
              rules="required"
              :placeholder="$t(`StructureSelectionView.placeholder-autre`)"
              :disabled="readonly"
              vid="typeAutreStructure"
              class="StructureUpdateView-autreType"
            />

            <GroupCheckboxesWithValidation
              v-model="checkedTetesStructure"
              :label="$t('StructureSelectionView.tete-structure')"
              :group="validationGroup"
              :data="tetesStructureLabels"
              :disabled="readonly"
              :vertical-display="true"
              :input-label="$t('StructureSelectionView.label-AUTRE')"
              :ext-input-value="localStructure.autreTeteStructure"
              :input-placeholder="$t(`StructureSelectionView.placeholder-autre-tete`)"
              vid="teteStructure"
              @manual-change="updateTetesStructure"
              @input-change="updateAutreTeteStructure"
            />

            <InputWithValidation
              v-model="localStructure.description"
              rules="required"
              type="textarea"
              :label="$t(`profilePage.structure.description`)"
              :placeholder="$t(`StructureSelectionView.placeholder-description`)"
              :disabled="readonly"
              vid="descriptionStructure"
            />

            <b-field :label="$t(`StructureSelectionView.label-agrement`)">
              <b-switch v-model="localStructure.agree" :disabled="readonly">
                {{
                  localStructure.agree
                    ? $t(`StructureSelectionView.label-agree`)
                    : $t(`StructureSelectionView.label-non-agree`)
                }}
              </b-switch>
            </b-field>

            <div v-if="localStructure.agree">
              <FileWithValidation
                rules="required"
                :input-notice="$t(`StructureSelectionView.label-info-agrement`)"
                :placeholder="$t(`StructureSelectionView.label-upload-agrement`)"
                :file-operation-url="agrementFileUrl"
                :model-value="localStructure.agrementFilePresent"
                vid="agrementStructure"
                :disabled="readonly"
                @delete-file="deleteAgrement"
                @file-uploaded-or-deleted="agrementUploaded"
              />
            </div>
          </ValidationObserver>
        </b-tab-item>

        <b-tab-item>
          <template #header>
            <TabHeaderWithHint
              :label="$t('profilePage.structure.adresse')"
              :tab-validation-hint="tabValidationHints[1]"
            />
          </template>
          <ValidationObserver ref="observer-1">
            <AdresseView
              v-model="localStructure"
              :readonly="readonly"
              :with-pays="true"
              :validation-group="validationGroup"
            />
          </ValidationObserver>
        </b-tab-item>
      </b-tabs>

      <div v-if="!readonly" class="buttons mt-1">
        <b-button icon-left="save" type="is-primary" @click="updateStructure">
          <span>{{ $t("profilePage.etablissement.save") }}</span>
        </b-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import AdresseView from "@/views/commons/AdresseView.vue"
import { StructureDTO, TeteStructure, TypeStructure } from "@/model/bean/GeneratedDTOs"
import { BOOLEAN, EnumHelper, TETES_STRINGS, TYPE_STRUCTURE } from "@/model/bean/EnumHelper"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import FileWithValidation from "@/views/inputs/FileWithValidation.vue"
import TabHeaderWithHint from "@/views/commons/TabHeaderWithHint.vue"
import AbstractFormStepWithTabs from "@/views/commons/AbstractFormStepWithTabs.vue"
import GroupCheckboxesWithValidation from "../inputs/GroupCheckBoxesWithValidation.vue"
import { BButton, BInput, BField, BSelect, BTabs, BTabItem, BSwitch } from "buefy"

export default defineComponent({
  name: "StructureUpdateView",
  components: {
    ValidationProvider,
    InputWithValidation,
    AdresseView,
    FileWithValidation,
    ValidationObserver,
    TabHeaderWithHint,
    GroupCheckboxesWithValidation,
    BButton,
    BInput,
    BField,
    BSelect,
    BTabs,
    BTabItem,
    BSwitch,
  },
  extends: AbstractFormStepWithTabs,
  props: {
    structure: {
      type: Object as () => StructureDTO,
      required: true,
    },
    userCanChange: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validationGroup: {
      type: String,
      default: "",
    },
    lowerTab: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["change-structure", "update-structure"],
  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      localStructure: { ...this.structure } as StructureDTO,
      displayOther: this.structure.type == TYPE_STRUCTURE.AUTRE,
      agrementFileUrl: "",
      tetesStructureLabels: Object.values(TETES_STRINGS).filter((t) => t !== TETES_STRINGS.AUTRE),
      checkedTetesStructure: [] as string[],
      structures: [] as StructureDTO[],
      structureTypes: Object.values(EnumHelper.allTypesStructure),
      agree: Object.values(BOOLEAN),
      // Properties from AbstractFormStepWithTabs
      numberOfTabs: 0,
      currentTabIndex: 0,
      tabValidationHints: [] as any[],
    }
  },
  computed: {
    structureTypesRadios(): TypeStructure[] {
      return Object.values(EnumHelper.allTypesStructure).filter(
        (type) => !this.isAireProtegee(type),
      )
    },
    structureTypesRadiosLabels(): string[] {
      return this.structureTypesRadios.map((type) => {
        return this.$t(
          `StructureSelectionView.types-label-${type.toLowerCase().replace(/_/g, "-")}`,
        ).toString()
      })
    },
    aireProtegeeTypes(): TypeStructure[] {
      return Object.values(EnumHelper.allTypesStructure).filter((type) => this.isAireProtegee(type))
    },
    aireProtegeeTypesLabels(): string[] {
      return this.aireProtegeeTypes.map((type) => {
        return this.$t(
          `StructureSelectionView.types-label-${type.toLowerCase().replace(/_/g, "-")}`,
        ).toString()
      })
    },
  },
  watch: {
    structure: {
      handler(newVal: StructureDTO) {
        this.localStructure = { ...newVal }
        this.displayOther = this.localStructure.type == TYPE_STRUCTURE.AUTRE
        this.checkedTetesStructure = this.getCheckedTetesStructure()
      },
      deep: true,
    },
  },
  created(): void {
    this.numberOfTabs = 2
    this.currentTabIndex = 0
    if (this.localStructure.id) {
      this.agrementFileUrl = "/organismes/structures/" + this.localStructure.id + "/agrement"
    } else {
      this.agrementFileUrl = "/organismes/structures/newAgrement"
    }
    this.checkedTetesStructure = this.getCheckedTetesStructure()
  },
  methods: {
    isAireProtegee(type: TypeStructure): boolean {
      return (
        type !== TYPE_STRUCTURE.ASSOCIATION &&
        type !== TYPE_STRUCTURE.COLLECTIVITE &&
        type !== TYPE_STRUCTURE.SYNDICAT_MIXTE &&
        type !== TYPE_STRUCTURE.ENTREPRISE_PRIVEE
      )
    },

    changeStructure(): void {
      this.infoReportingService.dialog(
        this.$t("profilePage.structure.change.confirmation").toString(),
        this.$t("profilePage.structure.change.are-you-sure").toString(),
        this.$t("profilePage.structure.change.save").toString(),
        () => this.validateChangeStructure(),
        "is-warning",
      )
    },

    validateChangeStructure(): void {
      this.$emit("change-structure")
    },

    setStructureType(value: TypeStructure): void {
      this.$nextTick(() => {
        this.localStructure.type = value
        this.displayOther = this.localStructure.type == TYPE_STRUCTURE.AUTRE
      })
    },

    updateStructureType(newValue: TypeStructure): void {
      this.setStructureType(newValue)
    },

    updateStructure(): void {
      this.updateTetesStructure()
      this.setWarnings()
      this.$emit("update-structure", this.localStructure)
    },

    deleteAgrement(): void {
      this.localStructure.agrementFilePresent = false
    },

    agrementUploaded(id: string): void {
      if (!this.localStructure.id) {
        this.localStructure.agrementFilePresent = true
        this.localStructure.agrementFile = id
      }
      this.setWarnings()
    },

    getCheckedTetesStructure(): string[] {
      if (!this.localStructure.tetesStructure || this.localStructure.tetesStructure.length === 0) {
        return []
      }
      return this.localStructure.tetesStructure.map((t) =>
        this.$t(`StructureSelectionView.label-${t}`).toString(),
      )
    },

    updateTetesStructure(): void {
      const tetes: TeteStructure[] = []

      if (this.checkedTetesStructure.includes(TETES_STRINGS.CEN)) {
        tetes.push("CEN")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.FCPN)) {
        tetes.push("FCPN")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.FNE)) {
        tetes.push("FNE")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.FPNR)) {
        tetes.push("FPNR")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.LPO)) {
        tetes.push("LPO")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.OFB)) {
        tetes.push("OFB")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.ONF)) {
        tetes.push("ONF")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.PNF)) {
        tetes.push("PNF")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.REN)) {
        tetes.push("REN")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.RGF)) {
        tetes.push("RGF")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.RNF)) {
        tetes.push("RNF")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.UICN)) {
        tetes.push("UICN")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.UNCPIE)) {
        tetes.push("UNCPIE")
      }
      if (this.checkedTetesStructure.includes(TETES_STRINGS.AUTRE)) {
        tetes.push("AUTRE")
      }

      this.localStructure.tetesStructure = tetes
    },

    updateAutreTeteStructure(value: string): void {
      this.localStructure.autreTeteStructure = value
    },

    // Methods from AbstractFormStepWithTabs
    setWarnings(): void {
      for (let i = 0; i < this.numberOfTabs; i++) {
        this.handleTabWarnings(i)
      }
    },

    handleTabWarnings(tabIndex: number): void {
      if (!this.tabValidationHints[tabIndex]) {
        this.tabValidationHints[tabIndex] = { errorsCount: 0, warningsCount: 0 }
      }
      // Add logic for handling tab warnings if needed
    },

    setTabIndex(tabIndex: number): void {
      this.currentTabIndex = tabIndex
    },

    lastTabDisplayed(): boolean {
      return this.currentTabIndex === this.numberOfTabs - 1
    },

    firstTabDisplayed(): boolean {
      return this.currentTabIndex === 0
    },

    goToNextTab(): void {
      const nextIndex = this.lastTabDisplayed() ? this.currentTabIndex : this.currentTabIndex + 1
      this.setTabIndex(nextIndex)
    },

    goToPreviousTab(): void {
      const previousIndex = this.firstTabDisplayed()
        ? this.currentTabIndex
        : this.currentTabIndex - 1
      this.setTabIndex(previousIndex)
    },
  },
})
</script>

<style lang="scss">
.StructureUpdateView-autreType {
  .mandatory {
    display: none;
  }
}
</style>
